import { Button, Css, ModalBody, ModalFooter, ModalHeader, MultiSelectField, useModal } from "@homebound/beam";
import { ObjectState } from "@homebound/form-state";
import { useMemo, useState } from "react";
import { ConfigureBidGroupsStepQuery } from "src/routes/bids/plan-based-bid-package/components/2.ConfigureBidGroupsStep.generated";
import { DimensionTypeCheckboxes } from "src/routes/bids/plan-based-bid-package/components/DimensionTypeCheckboxes";
import { PersonalizationGroupsField } from "src/routes/bids/plan-based-bid-package/components/PersonalizationGroupsField";
import {
  buildDimensions,
  buildPersonalizationDimensions,
  PlanBasedBidPackageBidPlanGroup,
  PlanBasedBidPackageBidPlanGroupDimension,
  PlanBasedBidPackageFormState,
} from "src/routes/bids/plan-based-bid-package/utils";

type AddOptionModalProps = {
  formState: ObjectState<PlanBasedBidPackageFormState>;
  dimensionResults: ConfigureBidGroupsStepQuery["potentialBidPlanDimensions"];
  potentialBidPlanOptions: ConfigureBidGroupsStepQuery["bidPackage"]["potentialBidPlanOptions"];
  editingGroup?: ObjectState<PlanBasedBidPackageBidPlanGroup>;
};

export function AddOptionModal(props: AddOptionModalProps) {
  const { formState, dimensionResults, potentialBidPlanOptions, editingGroup } = props;
  const { closeModal } = useModal();
  const isEdit = !!editingGroup;
  const [selectedOptionIds, setSelectedOptionIds] = useState<string[]>(
    isEdit ? [editingGroup.globalOptionId.value!] : [],
  );
  // Seed the dimension type checkboxes from the edited option's current dimensions; start empty when creating.
  const [selectedTypeIds, setSelectedTypeIds] = useState<string[]>(
    isEdit
      ? (editingGroup?.dimensions.value ?? []).map((d) => d.globalOptionTypeId).filter((id): id is string => !!id)
      : [],
  );
  // Seed the personalization groups from the edited option's current dimensions; start empty when creating.
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>(
    isEdit
      ? (editingGroup?.dimensions.value ?? []).map((d) => d.globalOptionGroupId).filter((id): id is string => !!id)
      : [],
  );
  // Filter out options already used as a BidPlanGroup in this bid, the option being edited stays available.
  const availableOptions = useMemo(() => {
    const usedGlobalOptionIds = (formState.bidPlanGroups.value ?? [])
      .map((g) => g.globalOptionId)
      .filter((id) => !!id && id !== editingGroup?.globalOptionId.value);
    return potentialBidPlanOptions
      .map((o) => o.globalOption)
      .filter((go) => !usedGlobalOptionIds.includes(go.id))
      .sortByKey("name");
  }, [formState.bidPlanGroups, potentialBidPlanOptions, editingGroup]);

  // Combine the checked type-based and selected group-based dimensions, keeping any prior rows.
  function dimensionsFrom(existing: PlanBasedBidPackageBidPlanGroupDimension[]) {
    return [
      ...buildDimensions(selectedTypeIds, existing, dimensionResults),
      ...buildPersonalizationDimensions(selectedGroupIds, existing, dimensionResults),
    ];
  }

  function save() {
    if (isEdit && editingGroup) {
      editingGroup.dimensions.set(dimensionsFrom(editingGroup.dimensions.value ?? []));
    } else {
      if (selectedOptionIds.isEmpty) return;
      const groups = formState.bidPlanGroups.value ?? [];
      const newOptionIds = availableOptions.filter((o) => selectedOptionIds.includes(o.id)).map((o) => o.id);
      // New options are added to the top of the options list (orders 1..N), so existing options are bumped down.
      // The base plan (no globalOptionId) keeps its order 0.
      const bumped = groups.map((g) => (g.globalOptionId ? { ...g, order: (g.order ?? 0) + newOptionIds.length } : g));
      // Each selected option becomes its own independent group sharing the same dimensions.
      const newGroups = newOptionIds.map((globalOptionId, i) => ({
        globalOptionId,
        order: i + 1,
        dimensions: dimensionsFrom([]),
      }));
      formState.bidPlanGroups.set([...bumped, ...newGroups]);
    }
    closeModal();
  }

  return (
    <>
      <ModalHeader>{isEdit ? "Edit Option" : "Add Options"}</ModalHeader>
      <ModalBody>
        <div css={Css.mb3.$}>Select the options and attributes to include as lines in your bid sheet.</div>
        <div css={Css.df.fdc.gap3.$}>
          <MultiSelectField
            readOnly={isEdit}
            label={isEdit ? "Option" : "Options"}
            options={availableOptions}
            values={selectedOptionIds}
            onSelect={setSelectedOptionIds}
          />
          <DimensionTypeCheckboxes
            dimensionResults={dimensionResults}
            selectedTypeIds={selectedTypeIds}
            onChange={setSelectedTypeIds}
          />
          <PersonalizationGroupsField
            dimensionResults={dimensionResults}
            selectedGroupIds={selectedGroupIds}
            onChange={setSelectedGroupIds}
          />
        </div>
      </ModalBody>
      <ModalFooter>
        <Button label="Cancel" variant="tertiary" onClick={closeModal} />
        <Button label="Save" onClick={save} disabled={selectedOptionIds.isEmpty} />
      </ModalFooter>
    </>
  );
}
